import React, { ReactNode } from 'react'
import _ from 'lodash'
import {
  Modal as SemanticModal,
  ModalProps as SemanticModalProps,
} from 'semantic-ui-react'
import classnames from 'classnames/bind'
import { AutomationTarget } from '../../../types'
import { lightenColor, getCssColorFromColorName } from '../../../utils/colors'
import Icon from '../../elementals/icon'
import { ColorNames } from '../../../constants/colorVars'
import useBreakpoints from '../../../hooks/useBreakpoints'
import SlideUpPanel from './slideUp'
import styles from './styles.scss'
const cx = classnames.bind(styles)

// TODO: convert Button to TypeScript
enum ButtonColorNames {
  Purple = ColorNames.Purple,
  Info = ColorNames.Info,
  Error = ColorNames.Error,
  Caution = ColorNames.Caution,
  Gray = ColorNames.Gray,
  Success = ColorNames.Success,
}

const hasHeader = (children: ReactNode): boolean =>
  Boolean(
    _.find(
      React.Children.toArray(children).filter(
        child => typeof child === 'object' && child !== null
      ),
      child => _.get(child, 'type.name') === 'Header'
    )
  )

export interface ModalProps extends SemanticModalProps, AutomationTarget {
  children?: ReactNode
  closeIcon?: boolean | object | ReactNode
  dimmer?: boolean | string
  fullScreen?: 'mobileOnly' | 'always' | 'never'
  mobileTitleSkin?: 'dark' | 'light'
  gradientSkin?: boolean
  gradientColor?: ButtonColorNames | ColorNames.White
  title?: ReactNode | string
  className?: string
  slideUp?: 'mobileOnly' | 'always' | 'never'
}

const Modal: React.FC<ModalProps> = ({
  elmId = 'modal',
  closeIcon,
  dimmer = true,
  fullScreen = 'mobileOnly',
  title,
  children,
  mobileTitleSkin = 'light',
  gradientSkin = false,
  gradientColor = ColorNames.Purple,
  className: modalClassName,
  size,
  slideUp = 'never',
  open,
  onClose,
  ...rest
}) => {
  const breakpoint = useBreakpoints()
  const isMobile = breakpoint === 'mobile'

  // Determine if we should use the slide-up panel
  // fullScreen takes precedence over slideUp
  const useSlideUpPanel =
    fullScreen !== 'mobileOnly' &&
    fullScreen !== 'always' &&
    (slideUp === 'always' || (slideUp === 'mobileOnly' && isMobile))

  // Render SlideUpPanel when conditions are met
  if (useSlideUpPanel) {
    return (
      <SlideUpPanel
        className={modalClassName}
        dimmer={typeof dimmer === 'boolean' ? dimmer : true}
        onClose={
          onClose
            ? () => onClose({} as React.MouseEvent<HTMLElement>, {})
            : undefined
        }
        open={open}
        size={size}
        title={title}
      >
        {children}
      </SlideUpPanel>
    )
  }

  const closeIconDefault = (
    <Icon
      className='close'
      data-elm-id={`${elmId}_close_button`}
      name='times'
      iconStyle='regular'
      fitted
    />
  )

  const finalCloseIcon = closeIcon !== undefined ? closeIcon : closeIconDefault

  // extract raw color values from color names
  const backgroundColor = ColorNames.White
  const gradientColorRaw = getCssColorFromColorName(gradientColor as ColorNames)
  const backgroundColorRaw = getCssColorFromColorName(backgroundColor)

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const lightGradientColor: string = lightenColor(gradientColorRaw, 50)
  const superLightGradientColor: string = lightenColor(gradientColorRaw, 80)
  const gradientStyle = !gradientSkin
    ? {}
    : {
        background:
          `linear-gradient(to bottom, ${backgroundColorRaw}, ${superLightGradientColor}) padding-box, ` +
          `linear-gradient(to bottom, ${lightGradientColor}, ${superLightGradientColor}) border-box`,
      }

  return (
    <SemanticModal
      data-elm-id={elmId}
      className={cx(
        'modal',
        {
          'full-screen-mobile': fullScreen === 'mobileOnly',
          'full-screen-always': fullScreen === 'always',
          'full-screen-never': fullScreen === 'never',
          'mobile-title-dark': mobileTitleSkin === 'dark' && title,
          'gradient-skin': gradientSkin,
          'default-header': !hasHeader(children),
        },
        modalClassName
      )}
      closeIcon={finalCloseIcon}
      dimmer={dimmer}
      size={size}
      style={{ ...gradientStyle }}
      open={open}
      onClose={onClose}
      {...rest}
    >
      {title && (
        <SemanticModal.Header data-elm-id={`${elmId}_header`}>
          {title}
        </SemanticModal.Header>
      )}
      {children}
    </SemanticModal>
  )
}

interface ModalStatic {
  Actions: typeof SemanticModal.Actions
  Content: typeof SemanticModal.Content
  Description: typeof SemanticModal.Description
  Header: typeof SemanticModal.Header
}

const ModalWithStatic: React.FC<ModalProps> & ModalStatic =
  Modal as React.FC<ModalProps> & ModalStatic

ModalWithStatic.Actions = SemanticModal.Actions
ModalWithStatic.Content = SemanticModal.Content
ModalWithStatic.Description = SemanticModal.Description
ModalWithStatic.Header = SemanticModal.Header

export * from './actionModalButton'
export default ModalWithStatic
